export type KeyModifier = "ctrl" | "shift" | "alt" | "meta";

export interface NormalizedKeyEvent {
  key: string;
  code: string;
  modifiers: Set<KeyModifier>;
  originalEvent: KeyboardEvent;
  timestamp: number;
}

export interface HotkeyConfig {
  key: string;
  modifiers?: KeyModifier[];
  scope?: string;
  description: string;
  handler(event: NormalizedKeyEvent): void | Promise<void>;
  enabled?: boolean;
  allowWhenInputFocused?: boolean;
  preventDefault?: boolean;
  stopPropagation?: boolean;
  category?: string;
  priority?: number;
}

export interface UserPreferences {
  enabled: boolean;
  requireModifiers: boolean;
  customMappings: Record<string, HotkeyConfig>;
  showIndicators: boolean;
  announceToScreenReader: boolean;
  disableWhenInputFocused: boolean;
  disabledShortcuts: string[];
}

export interface HotkeyScope {
  id: string;
  name: string;
  element: HTMLElement | null;
  activeHotkeys: Set<string>;
  priority: number;
  active: boolean;
}

export interface FocusTrapConfig {
  element: HTMLElement;
  allowEscape?: boolean;
  initialFocus?: HTMLElement;
  returnFocus?: HTMLElement;
  onActivate?(): void;
  onDeactivate?(): void;
}

export interface FocusTrap {
  activate(): void;
  deactivate(): void;
  isActive(): boolean;
}

export interface HotkeyStorage {
  get<T>(key: string): T | null;
  set<T>(key: string, value: T): void;
  remove(key: string): void;
  clear(): void;
}

export interface ValidationResult {
  valid: boolean;
  violations: ValidationViolation[];
  warnings: ValidationWarning[];
}

export interface ValidationViolation {
  criterion: string;
  level: "A" | "AA" | "AAA";
  message: string;
  suggestion: string;
  affectedHotkeys: string[];
}

export interface ValidationWarning {
  message: string;
  suggestion: string;
  affectedHotkeys: string[];
}

export interface ConflictDetection {
  hasConflicts: boolean;
  conflicts: HotkeyConflict[];
}

export interface HotkeyConflict {
  key: string;
  modifiers: KeyModifier[];
  scope: string | undefined;
  conflictingHotkeys: Array<{
    id: string;
    description: string;
    priority: number;
  }>;
}

export interface HotkeyManagerConfig {
  enableByDefault?: boolean;
  requireModifiers?: boolean;
  persistPreferences?: boolean;
  storage?: HotkeyStorage;
  globalScope?: string;
  autoResolveConflicts?: boolean;
  logWarnings?: boolean;
}

export interface ScreenReaderAnnouncer {
  announce(message: string, priority?: "polite" | "assertive"): void;
  announceHotkey(description: string, keys: string): void;
  clear(): void;
}

export interface HotkeyHelp {
  show(scope?: string): void;
  hide(): void;
  toggle(scope?: string): void;
  isVisible(): boolean;
}

export interface HotkeyEvents {
  "hotkey:registered": (config: HotkeyConfig) => void;
  "hotkey:unregistered": (id: string) => void;
  "hotkey:triggered": (config: HotkeyConfig, event: NormalizedKeyEvent) => void;
  "hotkey:invalid": (config: HotkeyConfig, error: Error) => void;
  "preferences:toggled": (enabled: boolean) => void;
  "hotkey:remapped": (original: HotkeyConfig, updated: HotkeyConfig) => void;
  "scope:activated": (scope: HotkeyScope) => void;
  "scope:deactivated": (scope: HotkeyScope) => void;
}

export interface HotkeyRegistryEntry {
  id: string;
  config: HotkeyConfig;
  originalConfig: HotkeyConfig;
  scope: string;
  enabled: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface HotkeyMatch {
  entry: HotkeyRegistryEntry;
  exactMatch: boolean;
  score: number;
}

const keyModifierCache = new Set<string>(["ctrl", "shift", "alt", "meta"]);

export function isValidKeyModifier(value: string): value is KeyModifier {
  return keyModifierCache.has(value);
}

export function isValidKeyModifierArray(value: unknown[]): value is KeyModifier[] {
  return Array.isArray(value) && value.every((v): v is KeyModifier => typeof v === "string" && isValidKeyModifier(v));
}

export function isValidHotkeyConfig(value: unknown): value is HotkeyConfig {
  if (!value || typeof value !== "object") return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const config = value as any;

  return (
    typeof config.key === "string"
    && typeof config.description === "string"
    && typeof config.handler === "function"
    && (config.modifiers === undefined || isValidKeyModifierArray(config.modifiers))
    && (config.scope === undefined || typeof config.scope === "string")
    && (config.enabled === undefined || typeof config.enabled === "boolean")
    && (config.allowWhenInputFocused === undefined || typeof config.allowWhenInputFocused === "boolean")
    && (config.preventDefault === undefined || typeof config.preventDefault === "boolean")
    && (config.stopPropagation === undefined || typeof config.stopPropagation === "boolean")
    && (config.category === undefined || typeof config.category === "string")
    && (config.priority === undefined || typeof config.priority === "number")
  );
}

export function isValidUserPreferences(value: unknown): value is UserPreferences {
  if (!value || typeof value !== "object") return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const prefs = value as any;

  return (
    typeof prefs.enabled === "boolean"
    && typeof prefs.requireModifiers === "boolean"
    && typeof prefs.customMappings === "object"
    && typeof prefs.showIndicators === "boolean"
    && typeof prefs.announceToScreenReader === "boolean"
    && typeof prefs.disableWhenInputFocused === "boolean"
    && Array.isArray(prefs.disabledShortcuts)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    && prefs.disabledShortcuts.every((id: any) => typeof id === "string")
  );
}

export function isValidHotkeyScope(value: unknown): value is HotkeyScope {
  if (!value || typeof value !== "object") return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const scope = value as any;

  return (
    typeof scope.id === "string"
    && typeof scope.name === "string"
    && (scope.element === null || scope.element instanceof HTMLElement)
    && scope.activeHotkeys instanceof Set
    && typeof scope.priority === "number"
    && typeof scope.active === "boolean"
  );
}

let cachedDefaultHotkeyConfig: Partial<HotkeyConfig> | null = null;
export function createDefaultHotkeyConfig(overrides?: Partial<HotkeyConfig>): Partial<HotkeyConfig> {
  if (cachedDefaultHotkeyConfig === null) {
    cachedDefaultHotkeyConfig = Object.freeze({
      modifiers: [],
      enabled: true,
      allowWhenInputFocused: false,
      preventDefault: true,
      stopPropagation: false,
      priority: 0,
    });
  }

  return overrides ? { ...cachedDefaultHotkeyConfig, ...overrides } : cachedDefaultHotkeyConfig;
}

let cachedDefaultUserPreferences: UserPreferences | null = null;
export function createDefaultUserPreferences(): UserPreferences {
  if (cachedDefaultUserPreferences === null) {
    cachedDefaultUserPreferences = Object.freeze({
      enabled: true,
      requireModifiers: false,
      customMappings: {},
      showIndicators: true,
      announceToScreenReader: true,
      disableWhenInputFocused: true,
      disabledShortcuts: [],
    });
  }

  return { ...cachedDefaultUserPreferences };
}

let cachedDefaultHotkeyScopeBase: Omit<HotkeyScope, "id" | "name" | "element"> | null = null;
export function createDefaultHotkeyScope(id: string, name: string, element: HTMLElement | null = null): HotkeyScope {
  if (cachedDefaultHotkeyScopeBase === null) {
    cachedDefaultHotkeyScopeBase = Object.freeze({
      activeHotkeys: new Set<string>(),
      priority: 0,
      active: false,
    });
  }

  return {
    id,
    name,
    element,
    ...cachedDefaultHotkeyScopeBase,
    activeHotkeys: new Set(), //* Always create new Set for uniqueness
  };
}

const normalizedModifiersCache = new Map<string, KeyModifier[]>();
export function normalizeKeyModifiers(modifiers: string[] | undefined): KeyModifier[] {
  if (!modifiers || modifiers.length === 0) return [];

  const key = modifiers.sort().join(",");
  if (normalizedModifiersCache.has(key)) {
    return normalizedModifiersCache.get(key)!;
  }

  const normalized = modifiers.filter(isValidKeyModifier) as KeyModifier[];
  const sorted = [...new Set(normalized)].sort(); // Remove duplicates and sort

  normalizedModifiersCache.set(key, sorted);
  return sorted;
}

const shortcutStringCache = new Map<string, string>();
export function createShortcutString(key: string, modifiers: KeyModifier[] = []): string {
  const normalizedModifiers = normalizeKeyModifiers(modifiers);
  const cacheKey = `${normalizedModifiers.join("+")}+${key}`;

  if (shortcutStringCache.has(cacheKey)) {
    return shortcutStringCache.get(cacheKey)!;
  }

  const parts: string[] = [...normalizedModifiers];
  if (key) parts.push(key);

  const shortcutString = parts.join("+");
  shortcutStringCache.set(cacheKey, shortcutString);
  return shortcutString;
}

export function hotkeyConfigsConflict(config1: HotkeyConfig, config2: HotkeyConfig): boolean {
  if (config1 === config2) return true;

  if (config1.scope !== config2.scope) return false;

  if (config1.key !== config2.key) return false;

  const mods1 = normalizeKeyModifiers(config1.modifiers);
  const mods2 = normalizeKeyModifiers(config2.modifiers);

  if (mods1.length !== mods2.length) return false;

  return mods1.every((mod, index) => mod === mods2[index]);
}

export function clearTypeUtilityCaches(): void {
  normalizedModifiersCache.clear();
  shortcutStringCache.clear();
  cachedDefaultHotkeyConfig = null;
  cachedDefaultUserPreferences = null;
  cachedDefaultHotkeyScopeBase = null;
}

export function getTypeUtilityCacheStats() {
  return {
    normalizedModifiersCache: normalizedModifiersCache.size,
    shortcutStringCache: shortcutStringCache.size,
    hasCachedDefaultHotkeyConfig: cachedDefaultHotkeyConfig !== null,
    hasCachedDefaultUserPreferences: cachedDefaultUserPreferences !== null,
    hasCachedDefaultHotkeyScopeBase: cachedDefaultHotkeyScopeBase !== null,
    keyModifierCacheSize: keyModifierCache.size,
  };
}
